import type { PreviewReview } from '@flexbyt/shared';
import { WidgetShowcase } from '../WidgetShowcase.js';

/**
 * One ReviewsJet review, printed as a piece of the homepage.
 *
 * ===========================================================================
 * Why one, and why here
 * ===========================================================================
 * ReviewsJet's own reviews live on the pricing page now, as a grid, because
 * that is where somebody asks "is it worth paying for". This is a different
 * job: a single line of praise dropped into the homepage at the moment the
 * reader has just watched the product do its whole trick and is deciding
 * whether to believe it. A wall here would be the social-proof section moved
 * back; one quotation is a punctuation mark.
 *
 * It sits beside "Wherever your reviews already are", in the space that
 * section's heading block was leaving empty on a wide screen. That turned out
 * to be the right home for two reasons beyond filling a hole. The sources
 * section is the page's first factual claim — these are the platforms we read
 * — and a real customer's sentence next to it is the first evidence that the
 * claim is worth anything. And it is early: a single quotation belongs where
 * somebody is still deciding whether to keep reading, not four sections later
 * where they have already decided.
 *
 * ===========================================================================
 * It is the quote widget, doing the job the quote widget is for
 * ===========================================================================
 * Not a `<blockquote>` styled to look like one. The `note` template is a real
 * layout a customer can install, drawn by the same renderer as everything else
 * on this site — so the section is simultaneously the argument and a
 * demonstration of the widget making it. That is the whole reason ReviewsJet
 * shows its own reviews with its own product rather than typesetting them.
 *
 * `mode` stays at the default `preview`, which pins the widget to the *first*
 * review it is given. That matters here: it is handed exactly one, chosen by
 * name upstream, and a live-mode random pick over a single-element array would
 * be the same review by accident rather than on purpose.
 */
export function FeaturedQuote({ review }: { review: PreviewReview }) {
  return (
    /*
      No canvas, no ring, no padding of its own.

      It sat on `rj-canvas` — the dotted preview surface — on the argument that
      the note template is a card pinned a degree off true and wants something
      to be stuck *to*. That is true in the builder, where the dotted ground
      says "this is a component, here is what it looks like". It is wrong here.
      This is not a demonstration of a widget; it is one customer saying one
      thing, and framing it announced the quotation as a product feature rather
      than as somebody's opinion.

      The section it now sits in supplies the ground, and the tilt reads against
      the page itself.
    */
    <figure>
      <WidgetShowcase
        type="quote"
        reviews={[review]}
        settings={{
          /*
            Named rather than left to the default, even though `note` *is* the
            default. This section is about a particular shape — a small tilted
            card — and inheriting it would mean the homepage silently changed
            the day somebody changed the product's default template for an
            unrelated reason.
          */
          quoteTemplate: 'note',
        }}
      />
    </figure>
  );
}
